import React from 'react';
import { Container, Typography, Grid, Box, Card, CardContent, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WebIcon from '@mui/icons-material/Web';
import CodeIcon from '@mui/icons-material/Code';
import SpeedIcon from '@mui/icons-material/Speed';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import BrushIcon from '@mui/icons-material/Brush';
import SecurityIcon from '@mui/icons-material/Security';
import UpdateIcon from '@mui/icons-material/Update';
import SearchIcon from '@mui/icons-material/Search';

const features = [
  {
    icon: BrushIcon,
    title: 'Custom Design',
    description: 'Unique, modern designs tailored to your brand identity'
  },
  {
    icon: PhoneIphoneIcon,
    title: 'Responsive Design',
    description: 'Perfect viewing experience across all devices'
  },
  {
    icon: SpeedIcon,
    title: 'Performance Optimized',
    description: 'Fast loading speeds and smooth user experience'
  },
  {
    icon: SecurityIcon,
    title: 'Secure & Reliable',
    description: 'Built with the latest security best practices'
  },
  {
    icon: SearchIcon,
    title: 'SEO Friendly',
    description: 'Optimized for search engines to improve visibility'
  },
  {
    icon: UpdateIcon,
    title: 'Easy Updates',
    description: 'User-friendly content management system'
  },
  {
    icon: CodeIcon,
    title: 'Clean Code',
    description: 'Well-structured, maintainable codebase'
  },
  {
    icon: WebIcon,
    title: 'Modern Technologies',
    description: 'Built with the latest web technologies'
  }
];

const technologies = [
  'React.js',
  'Next.js',
  'Node.js',
  'TypeScript',
  'Material-UI',
  'Tailwind CSS',
  'MongoDB',
  'PostgreSQL',
  'AWS',
  'Firebase'
];

function WebsiteDevelopment() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: 'background.default', py: { xs: 4, sm: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.75rem' },
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}
          >
            Website Development
          </Typography>
          <Typography 
            variant="h5" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto',
              mb: 4,
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Transform your online presence with our professional website development services.
            We create modern, responsive, and user-friendly websites that help your business grow.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/booking')}
            sx={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              color: 'white',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #00B8D4 90%)'
              }
            }}
          >
            Get Started
          </Button>
        </Box>

        {/* Features Grid */}
        <Typography 
          variant="h3" 
          align="center" 
          gutterBottom
          sx={{ 
            mb: 4,
            fontSize: { xs: '2rem', sm: '2.5rem' }
          }}
        >
          Our Features
        </Typography>
        <Grid container spacing={4} sx={{ mb: { xs: 6, md: 8 } }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                className="glass-effect"
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '15px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <feature.icon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Technologies Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography 
            variant="h3" 
            gutterBottom
            sx={{ 
              mb: 4,
              fontSize: { xs: '2rem', sm: '2.5rem' }
            }}
          >
            Technologies We Use
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {technologies.map((tech, index) => (
              <Grid item key={index}>
                <Card
                  sx={{
                    px: 3,
                    py: 1,
                    background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 203, 243, 0.1) 100%)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <Typography variant="body1" color="primary">
                    {tech}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box 
          sx={{ 
            textAlign: 'center',
            p: { xs: 4, md: 6 },
            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 203, 243, 0.1) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.75rem', sm: '2rem' }
            }}
          >
            Ready to Build Your Dream Website?
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 600, 
              mx: 'auto', 
              mb: 3 
            }}
          >
            Let's create a website that perfectly represents your brand and helps you achieve your business goals.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/booking')}
            sx={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              color: 'white',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #00B8D4 90%)'
              }
            }}
          >
            Book a Consultation
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default WebsiteDevelopment;
